import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import type { FeatureCollection } from 'geojson'
import type * as maplibregl from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getLayerConfigs } from './layer-config'
import { useOverwatchMapOperatingAreasQuery } from './OperatingAreas.hook'
import {
  type AreaFeature,
  buildOutsideMask,
  mergeOperatingAreas,
  mergeOutlineFeatures,
} from './operatingAreas'
import { resolveRegionId } from './roadBlockagesUtils'

const MASK_SOURCE_ID = 'operating-areas-mask-source'
const MASK_LAYER_ID = 'operating-areas-mask-fill'
const OUTLINE_SOURCE_ID = 'operating-areas-outline-source'
/** Exported so the geonet z-ordering can keep this outline above geonet outlines. */
export const OPERATING_AREA_OUTLINE_LAYER_ID = 'operating-areas-outline'

// Same look as the Backoffice fleet map in light mode: a light grey wash over
// everything outside the operating area and a thin black outline around it.
const OUTLINE_COLOR = '#000000'
const MASK_COLOR = '#000000'
const MASK_OPACITY = 0.1
const OUTLINE_WIDTH = 1.5
const EMPTY_GEOJSON: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

// A failed live query falls back to the hardcoded outline instead of showing
// an error notification.
const ignoreError = () => {}

const removeLayers = (map: maplibregl.Map) => {
  for (const id of [OPERATING_AREA_OUTLINE_LAYER_ID, MASK_LAYER_ID]) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  for (const id of [OUTLINE_SOURCE_ID, MASK_SOURCE_ID]) {
    if (map.getSource(id)) map.removeSource(id)
  }
}

/**
 * The region's operating area. It comes from `operatingAreasByRegionId`, the
 * same data the Backoffice fleet map uses, so both apps show the same outline.
 * When the region is unknown, or the query fails or returns no area, the
 * metro's hardcoded geonet outline is used instead.
 *
 * `isLive` tells whether the live area was found; `settled` turns true once
 * that is known.
 */
export const useOperatingAreas = ({
  metropolisId,
  metropolisName,
  enabled,
}: {
  metropolisId: number
  metropolisName?: string
  enabled: boolean
}) => {
  const regionId = resolveRegionId(metropolisName)

  const { data, error } = useErrorHandlingQuery(
    useOverwatchMapOperatingAreasQuery,
    enabled && regionId
      ? { variables: { regionId }, onError: ignoreError }
      : { skip: true },
  )

  const liveArea = useMemo(
    () => mergeOperatingAreas(data?.operatingAreasByRegionId ?? []),
    [data],
  )
  const settled =
    !enabled || !regionId || error !== undefined || data !== undefined
  const needsFallback = enabled && settled && !liveArea

  const fallbackConfig = getLayerConfigs(metropolisId, metropolisName).find(
    config => config.type === 'geonet',
  )
  const [fallback, setFallback] = useState<{
    configId: string
    outline: FeatureCollection
  } | null>(null)

  useEffect(() => {
    if (!needsFallback || !fallbackConfig) return
    let cancelled = false
    void fallbackConfig.loader().then(outline => {
      if (!cancelled) setFallback({ configId: fallbackConfig.id, outline })
    })
    return () => {
      cancelled = true
    }
  }, [needsFallback, fallbackConfig])

  const fallbackOutline =
    needsFallback && fallback?.configId === fallbackConfig?.id
      ? fallback?.outline
      : undefined

  const fallbackArea = useMemo(
    () => (fallbackOutline ? mergeOutlineFeatures(fallbackOutline) : null),
    [fallbackOutline],
  )

  return { area: liveArea ?? fallbackArea, isLive: liveArea !== null, settled }
}

/**
 * Draws the operating area: a black outline around it and a grey mask outside
 * it. Shown hardcoded geonets are cut out of the mask too, so the unshaded part
 * grows to cover them while the outline stays the operating area's.
 */
export const useOperatingAreasLayers = ({
  map,
  area,
  shownGeonetOutlines,
  insertBeforeLayer,
}: {
  map: maplibregl.Map | null
  area: AreaFeature | null
  shownGeonetOutlines: ReadonlyArray<FeatureCollection>
  insertBeforeLayer?: string
}) => {
  const mask = useMemo(() => {
    if (!area) return null
    const geonetAreas = shownGeonetOutlines
      .map(mergeOutlineFeatures)
      .filter(geonetArea => geonetArea !== null)
    return buildOutsideMask([area, ...geonetAreas])
  }, [area, shownGeonetOutlines])

  // The layers are created once and their data swapped in place below. Removing
  // and re-adding a GeoJSON source leaves it empty until it is re-tiled, which
  // made the shade blink whenever a geonet was toggled.
  const hasArea = area !== null
  // Read when the layers are (re)created, so they start with the current data.
  const dataRef = useRef({ area, mask })
  dataRef.current = { area, mask }

  useEffect(() => {
    if (!map || !hasArea) return

    const beforeLayer =
      insertBeforeLayer && map.getLayer(insertBeforeLayer)
        ? insertBeforeLayer
        : undefined

    map.addSource(MASK_SOURCE_ID, {
      type: 'geojson',
      data: dataRef.current.mask ?? EMPTY_GEOJSON,
    })
    map.addLayer(
      {
        id: MASK_LAYER_ID,
        type: 'fill',
        source: MASK_SOURCE_ID,
        paint: { 'fill-color': MASK_COLOR, 'fill-opacity': MASK_OPACITY },
      },
      beforeLayer,
    )
    map.addSource(OUTLINE_SOURCE_ID, {
      type: 'geojson',
      data: dataRef.current.area ?? EMPTY_GEOJSON,
    })
    map.addLayer(
      {
        id: OPERATING_AREA_OUTLINE_LAYER_ID,
        type: 'line',
        source: OUTLINE_SOURCE_ID,
        paint: {
          'line-color': OUTLINE_COLOR,
          'line-width': OUTLINE_WIDTH,
        },
        layout: { 'line-join': 'round' },
      },
      beforeLayer,
    )

    return () => removeLayers(map)
  }, [map, hasArea, insertBeforeLayer])

  useEffect(() => {
    if (!map || !area) return
    map.getSource<maplibregl.GeoJSONSource>(OUTLINE_SOURCE_ID)?.setData(area)
    map
      .getSource<maplibregl.GeoJSONSource>(MASK_SOURCE_ID)
      ?.setData(mask ?? EMPTY_GEOJSON)
  }, [map, area, mask])
}
